import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useCampaign } from "../../hooks/use-campaigns";
import {
  useDepositBalance,
  useCreateDepositChargeOrder,
  useCreateCampaignParticipationWithoutCharge,
} from "../../hooks/use-campaign-payment";
import { SkeletonWave } from "../../../../components/common/skeleton-wave";
import BACK_IMG from "../../../../components/common/back-img.png";
import KakaoLogo from "../../../../assets/images/kakao-logo.png";
import NaverLogo from "../../../../assets/images/naver-logo.png";
import PaymentLoading from "../../../../components/payment/payment-loading";
import { clampNicePayGoodsName } from "../../../../utils/nice-pay-goods-name";
import DraggableBottomSheet from "../../../../components/common/draggable-bottom-sheet";

const CampaignJoinPage = () => {
  const { campaignId } = useParams<{ campaignId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const campaignIdNum = campaignId ? parseInt(campaignId) : 0;
  const { campaign, loading: campaignLoading } = useCampaign(campaignIdNum);
  const {
    balance,
    loading: balanceLoading,
    refetch: refetchBalance,
  } = useDepositBalance();
  const { createDepositChargeOrder, loading: orderLoading } =
    useCreateDepositChargeOrder();
  const {
    createCampaignParticipationWithoutCharge,
    loading: participationLoading,
  } = useCreateCampaignParticipationWithoutCharge();

  const [selectedPayment, setSelectedPayment] = useState("card");
  const [isAllAgreed, setIsAllAgreed] = useState(false);
  const [isPrivacyAgreed, setIsPrivacyAgreed] = useState(false);
  const [isCancelAgreed, setIsCancelAgreed] = useState(false);
  const [isPaymentLoading, setIsPaymentLoading] = useState(false);
  const [isDepositExplanationOpen, setIsDepositExplanationOpen] = useState(false);

  // 미래의 회차만 필터링 (날짜가 지나지 않은 것만)
  const availableRuns =
    campaign?.runs?.filter((run: any) => {
      const now = new Date();
      const runEndAt = new Date(run.endAt);
      return run.isActive && runEndAt > now;
    }) || [];

  // URL에서 받은 runId 또는 첫 번째 회차
  const urlRunId = searchParams.get("runId");
  const selectedRun =
    (urlRunId
      ? availableRuns.find((run: any) => run.id === parseInt(urlRunId))
      : null) ||
    availableRuns[0] ||
    null;

  // 예치금 계산
  const depositAmount = selectedRun?.depositAmount || 0;
  const chargeAmount = Math.max(0, depositAmount - balance);

  // 마감 여부 확인
  const isFull = selectedRun?.maxParticipants !== null && selectedRun?.isFull;

  // 취소 불가 여부 확인 (캠페인 시작 2일 전까지 취소 가능)
  const isCancelDisabled = useMemo(() => {
    if (!selectedRun?.startAt) return false;
    
    const now = new Date();
    const startAt = new Date(selectedRun.startAt);
    
    // 시작일의 2일 전 00:00:00 계산
    const twoDaysBeforeStart = new Date(startAt.getTime() - 2 * 24 * 60 * 60 * 1000);
    twoDaysBeforeStart.setHours(0, 0, 0, 0);
    
    // 현재 시간이 2일 전 이전이면 취소 가능 (즉, 2일 전 이후면 취소 불가)
    // 현재 시간 > 2일 전 00:00:00 이면 취소 불가
    return now > twoDaysBeforeStart;
  }, [selectedRun?.startAt]);

  // 전체 동의 처리
  useEffect(() => {
    if (isAllAgreed) {
      setIsPrivacyAgreed(true);
      setIsCancelAgreed(true);
    }
  }, [isAllAgreed]);

  // 개별 동의 시 전체 동의 해제
  useEffect(() => {
    if (!isPrivacyAgreed || !isCancelAgreed) {
      setIsAllAgreed(false);
    } else {
      setIsAllAgreed(true);
    }
  }, [isPrivacyAgreed, isCancelAgreed]);

  useEffect(() => {
    if (campaign && selectedRun) {
      refetchBalance();
    }
  }, [campaign, selectedRun, refetchBalance]);

  const handlePayment = async () => {
    if (!isPrivacyAgreed || !isCancelAgreed) {
      alert("약관에 동의해주세요.");
      return;
    }

    if (!selectedRun) {
      alert("구매 시간을 선택해주세요.");
      return;
    }

    if (isFull) {
      alert("죄송합니다. 참여 인원이 마감되었습니다.");
      navigate(`/campaigns/${campaignId}`);
      return;
    }

    // 보유 예치금이 충분한 경우: 바로 참여 생성
    if (chargeAmount === 0) {
      try {
        setIsPaymentLoading(true);
        const result = await createCampaignParticipationWithoutCharge({
          variables: {
            campaignRunId: selectedRun.id,
          },
        });

        const participationId =
          result.data?.createCampaignParticipationWithoutCharge
            ?.participationId;
        if (participationId) {
          navigate(
            `/nice-result-for-deposit?ok=1&amount=0&participationId=${participationId}`
          );
        } else {
          throw new Error("참여 생성에 실패했습니다.");
        }
      } catch (error: any) {
        console.error("참여 생성 오류:", error);
        setIsPaymentLoading(false);
        const errorCode =
          error.graphQLErrors?.[0]?.extensions?.code ||
          error.networkError?.result?.errors?.[0]?.extensions?.code;
        if (errorCode === "PARTICIPATION_FULL") {
          alert("죄송합니다. 참여 인원이 마감되었습니다.");
          navigate(`/campaigns/${campaignId}`);
        } else {
          alert(error.message || "참여 생성 중 오류가 발생했습니다.");
        }
      }
      return;
    }

    // 충전이 필요한 경우
    try {
      setIsPaymentLoading(true);
      const result = await createDepositChargeOrder({
        variables: {
          input: {
            amount: chargeAmount,
            campaignRunId: selectedRun.id,
          },
        },
      });

      const orderData = result.data?.createDepositChargeOrder;
      if (!orderData) {
        throw new Error("주문 정보를 받을 수 없습니다.");
      }

      const method =
        selectedPayment === "card"
          ? "card"
          : selectedPayment === "kakaopay"
          ? "kakaopay"
          : "naverpayCard";

      const clientId = orderData.nicePayClientKey;

      // NicePay 결제 창 호출
      if (window.AUTHNICE) {
        window.AUTHNICE.requestPay({
          clientId,
          method,
          orderId: orderData.orderId,
          amount: orderData.amount,
          appScheme: "mahi://",
          fnError: () => {
            setIsPaymentLoading(false);
            navigate(
              "/nice-result-for-deposit?ok=0&message=결제에 실패했습니다"
            );
          },
          goodsName: clampNicePayGoodsName(
            `${campaign?.productName || "캠페인"} 예치금 충전`
          ),
          returnUrl: orderData.returnUrl,
          mallReserved: orderData.mallReserved,
        });
      } else {
        throw new Error("결제 모듈을 불러올 수 없습니다.");
      }
    } catch (error: any) {
      console.error("결제 오류:", error);
      setIsPaymentLoading(false);
      alert(error.message || "결제 중 오류가 발생했습니다.");
    }
  };

  if (campaignLoading || balanceLoading) {
    return (
      <div className="min-h-screen bg-white p-4">
        <SkeletonWave className="pt-8" />
      </div>
    );
  }

  if (!campaign) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-4">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-800 mb-4">
            캠페인을 찾을 수 없습니다
          </h1>
          <button
            onClick={() => navigate(`/campaigns/${campaignId}`)}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            돌아가기
          </button>
        </div>
      </div>
    );
  }

  if (availableRuns.length === 0) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-4">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-800 mb-4">
            참여 가능한 회차가 없습니다
          </h1>
          <button
            onClick={() => navigate(`/campaigns/${campaignId}`)}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            돌아가기
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white min-h-screen flex flex-col relative">
      {isPaymentLoading && <PaymentLoading isVisible={isPaymentLoading} />}
      
      {/* 헤더 */}
      <div className="px-4 pt-4 pb-3 border-b border-gray-200">
        <img
          src={BACK_IMG}
          onClick={() => navigate(-1)}
          className="w-[40px] h-[40px] mb-2 cursor-pointer"
          alt="back"
        />
        <h1 className="text-xl font-bold text-black">참가하기</h1>
      </div>

      {/* 취소 불가 안내 배너 */}
      {isCancelDisabled && (
        <div className="bg-gray-700 text-white px-4 py-3 text-center">
          <p className="text-sm font-medium mb-1">미션 취소 불가 기간이에요.</p>
          <p className="text-sm">참가 신청 후 취소할 수 없어요.</p>
        </div>
      )}

      <div className="flex-1 overflow-y-auto pb-32">
        {/* 제품 정보 섹션 - 가로 레이아웃 */}
        <div className="px-4 py-4 border-b border-gray-200">
          <div className="flex gap-3">
            {campaign.productImageUrl && (
              <img
                src={campaign.productImageUrl}
                alt={campaign.productName}
                className="w-20 h-20 object-cover rounded-lg border border-gray-200 flex-shrink-0"
              />
            )}
            <div className="flex-1 min-w-0">
              <p className="text-sm text-black font-medium mb-1.5 line-clamp-2">
                {campaign.productName}
              </p>
              <div className="flex items-center gap-2 mb-1">
                <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded text-xs font-bold">
                  {campaign.paybackPrice.toLocaleString()}원 페이백
                </span>
                <span className="text-blue-600 text-xs font-medium">
                  실구매가 {campaign.discountPrice.toLocaleString()}원
                </span>
              </div>
              {campaign.originalPrice > campaign.discountPrice && (
                <span className="text-gray-400 line-through text-xs">
                  {campaign.originalPrice.toLocaleString()}원
                </span>
              )}
            </div>
          </div>
        </div>

        {/* 예치금 섹션 */}
        <div className="px-4 py-4 border-b border-gray-200">
          <div className="mb-3">
            <p className="text-sm text-gray-600 mb-1">
              인증 성공하면 예치금 {depositAmount.toLocaleString()}원 환급 +{" "}
              {selectedRun?.prizeAmount.toLocaleString()}원 페이백
            </p>
            <div className="flex items-baseline gap-2">
              <span className="text-2xl font-bold text-black">
                {depositAmount.toLocaleString()}원
              </span>
              <span className="text-xs text-gray-500">(고정)</span>
            </div>
          </div>
          <div className="border-t border-blue-200 pt-3">
            <button
              onClick={() => setIsDepositExplanationOpen(true)}
              className="w-full text-center py-2 px-3 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-100"
            >
              예치금을 왜 걸어야 하나요?
            </button>
          </div>
        </div>

        {/* 예치금 충전 및 결제 섹션 */}
        <div className="px-4 py-4 border-b border-gray-200">
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">참가 예치금</span>
              <span className="text-sm font-semibold text-black">
                {depositAmount.toLocaleString()}원
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">사용 예치금</span>
              <div className="flex flex-col items-end">
                <span className="text-sm font-semibold text-black">
                  -{balance.toLocaleString()}원
                </span>
                <span className="text-xs text-gray-500 mt-0.5">
                  (현재 보유 예치금: {balance.toLocaleString()}원)
                </span>
              </div>
            </div>
            <div className="flex justify-between items-center pt-2 border-t border-gray-200">
              <span className="text-base font-bold text-black">충전 금액</span>
              <span className="text-xl font-bold text-blue-600">
                {chargeAmount.toLocaleString()}원
              </span>
            </div>
          </div>
        </div>

        {/* 결제 수단 선택 */}
        <div className="px-4 py-4 border-b border-gray-200">
          <div className="flex flex-col space-y-3">
            <label
              className={`flex items-center space-x-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                selectedPayment === "card"
                  ? "bg-blue-50 border-blue-500 shadow-sm"
                  : "bg-white border-gray-200 hover:border-gray-300"
              }`}
            >
              <input
                type="radio"
                name="payment"
                value="card"
                checked={selectedPayment === "card"}
                onChange={(e) => setSelectedPayment(e.target.value)}
                className="w-5 h-5 accent-blue-600"
              />
              <span className="text-black font-medium text-base">신용/체크카드</span>
            </label>

            <label
              className={`flex items-center space-x-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                selectedPayment === "kakaopay"
                  ? "bg-yellow-50 border-yellow-400 shadow-sm"
                  : "bg-white border-gray-200 hover:border-gray-300"
              }`}
            >
              <input
                type="radio"
                name="payment"
                value="kakaopay"
                checked={selectedPayment === "kakaopay"}
                onChange={(e) => setSelectedPayment(e.target.value)}
                className="w-5 h-5 accent-yellow-400"
              />
              <div className="flex items-center space-x-2">
                <img src={KakaoLogo} alt="카카오페이" className="h-6" />
                <span className="text-black font-medium text-base">카카오페이</span>
              </div>
            </label>

            <label
              className={`flex items-center space-x-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                selectedPayment === "naverpay"
                  ? "bg-green-50 border-green-500 shadow-sm"
                  : "bg-white border-gray-200 hover:border-gray-300"
              }`}
            >
              <input
                type="radio"
                name="payment"
                value="naverpay"
                checked={selectedPayment === "naverpay"}
                onChange={(e) => setSelectedPayment(e.target.value)}
                className="w-5 h-5 accent-green-600"
              />
              <div className="flex items-center space-x-2">
                <img
                  src={NaverLogo}
                  alt="네이버페이"
                  className="h-6 rounded-lg"
                />
                <span className="text-black font-medium text-base">네이버페이</span>
              </div>
            </label>
          </div>
          <p className="text-xs text-gray-500 mt-2">
            페이백 미션 참가 시, 가상계좌 이체는 지원되지 않습니다.
          </p>
        </div>

        {/* 약관 동의 섹션 */}
        <div className="px-4 py-4 border-b border-gray-200">
          <div className="space-y-3">
            {/* 전체 동의 */}
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={isAllAgreed}
                onChange={(e) => setIsAllAgreed(e.target.checked)}
                className="w-5 h-5 accent-blue-600"
              />
              <span className="text-sm text-black font-medium">
                필수 이용약관 전체 동의하기
              </span>
            </label>

            {/* 개인정보 제3자 제공 동의 */}
            <label className="flex items-center gap-2 cursor-pointer pl-7">
              <input
                type="checkbox"
                checked={isPrivacyAgreed}
                onChange={(e) => setIsPrivacyAgreed(e.target.checked)}
                className="w-5 h-5 accent-blue-600"
              />
              <span className="text-sm text-black">
                개인정보{" "}
                <span
                  onClick={(e) => {
                    e.preventDefault();
                    window.open(
                      "https://trapezoidal-hamburger-538.notion.site/3-2ea08e6e11b380718037ee264a4ff706",
                      "_blank",
                      "noopener,noreferrer"
                    );
                  }}
                  className="underline text-blue-600"
                >
                  제3자 제공에
                </span>
                대한 동의
              </span>
            </label>

            {/* 미션 취소 규정 동의 */}
            <div>
              <label className="flex items-start gap-2 cursor-pointer pl-7">
                <input
                  type="checkbox"
                  checked={isCancelAgreed}
                  onChange={(e) => setIsCancelAgreed(e.target.checked)}
                  className="w-5 h-5 accent-blue-600 mt-0.5"
                />
                <span className="text-sm text-black">
                  미션 취소 규정에 대한 동의
                </span>
              </label>
              {/* 취소 규정 상세 */}
              <div className="ml-12 mt-2 bg-gray-50 rounded-lg p-3 space-y-1.5">
                <div className="flex justify-between text-xs">
                  <span className="text-gray-600">당일 취소:</span>
                  <span className="text-red-500 font-semibold">취소 불가</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-gray-600">1일 전 취소:</span>
                  <span className="text-red-500 font-semibold">취소 불가</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-gray-600">2일 전 취소:</span>
                  <span className="text-green-600 font-semibold">100% 취소 가능</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 하단 약관 링크 */}
        <div className="px-4 py-4">
          <p className="text-xs text-gray-500 mb-2">
            결제 조건 및 서비스 약관에 동의합니다
          </p>
          <div className="flex gap-4">
            <button
              onClick={() => {
                window.open(
                  "https://trapezoidal-hamburger-538.notion.site/2ea08e6e11b380f5b8c3c730058a13c7?pvs=74",
                  "_blank",
                  "noopener,noreferrer"
                );
              }}
              className="text-xs text-gray-500 underline"
            >
              미션 취소 규정
            </button>
            <button
              onClick={() => {
                window.open(
                  "https://trapezoidal-hamburger-538.notion.site/2ea08e6e11b38091b463e49c6388c722",
                  "_blank",
                  "noopener,noreferrer"
                );
              }}
              className="text-xs text-gray-500 underline"
            >
              참가규칙
            </button>
            <button
              onClick={() => {
                window.open(
                  "https://trapezoidal-hamburger-538.notion.site/2ea08e6e11b380f5b8c3c730058a13c7?pvs=74",
                  "_blank",
                  "noopener,noreferrer"
                );
              }}
              className="text-xs text-gray-500 underline"
            >
              이용약관
            </button>
          </div>
        </div>
      </div>

      {/* 하단 CTA 버튼 */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4">
        {isFull && (
          <div className="mb-3 text-center">
            <p className="text-red-600 font-semibold text-sm">
              참여 인원이 마감되었습니다.
            </p>
            {selectedRun?.remainingSlots !== null && (
              <p className="text-gray-600 text-xs mt-1">
                현재 {selectedRun.currentParticipants}명 / 최대{" "}
                {selectedRun.maxParticipants}명
              </p>
            )}
          </div>
        )}
        {selectedRun?.maxParticipants !== null && !isFull && (
          <div className="mb-3 text-center">
            {selectedRun.remainingSlots !== null &&
              selectedRun.remainingSlots <= 5 && (
                <p className="text-orange-600 font-semibold text-sm">
                  남은 자리: {selectedRun.remainingSlots}명
                </p>
              )}
          </div>
        )}
        <button
          onClick={handlePayment}
          disabled={
            !isPrivacyAgreed ||
            !isCancelAgreed ||
            isPaymentLoading ||
            orderLoading ||
            participationLoading ||
            isFull
          }
          className={`w-full py-4 rounded-lg font-bold text-lg transition-colors ${
            !isPrivacyAgreed ||
            !isCancelAgreed ||
            isPaymentLoading ||
            orderLoading ||
            participationLoading ||
            isFull
              ? "bg-gray-300 text-gray-500 cursor-not-allowed"
              : "bg-blue-600 text-white hover:bg-blue-700"
          }`}
        >
          {isFull
            ? "마감되었습니다"
            : chargeAmount > 0
            ? `${chargeAmount.toLocaleString()}원 충전하기`
            : "참가하기"}
        </button>
      </div>

      {/* 예치금 설명 바텀시트 */}
      <DraggableBottomSheet
        isOpen={isDepositExplanationOpen}
        onClose={() => setIsDepositExplanationOpen(false)}
        buttonText="확인"
        onButtonClick={() => setIsDepositExplanationOpen(false)}
      >
        <div className="flex flex-col items-center pt-6 pb-4">
          {/* 핑키 프라미스 이모지 */}
          <div className="text-6xl mb-4">🙏</div>
          
          {/* 제목 */}
          <h2 className="text-xl font-bold text-black text-left w-full mb-3 px-4">
            미션 인증에 대한 약속의 의미로 예치금을 걸어요!
          </h2>
          
          {/* 설명 텍스트 */}
          <p className="text-base text-gray-700 leading-relaxed text-left w-full mb-4 px-4">
            인증을 성공하시면 페이백과 함께 예치금을 전액 환급 해 드리니 걱정 말고 참가하세요!
          </p>
          
          {/* 성공 시 혜택 */}
          <div className="text-left w-full px-4">
            <span className="text-base text-black">인증 성공시 </span>
            <span className="text-base font-semibold text-blue-600">
              예치금 {depositAmount.toLocaleString()}원 환급 + {selectedRun?.prizeAmount.toLocaleString()}원 페이백
            </span>
          </div>
        </div>
      </DraggableBottomSheet>
    </div>
  );
};

export default CampaignJoinPage;
